import { state, els } from "./state.js";
import { customIcon_1, customIcon_2, boundaryColor } from "./config.js";
import { toggleLoading } from "./utils.js";
import { fetchAndDisplayRoute, clearTempMarkers } from "./navigation.js";
import { ApiService } from "../../services/ApiService.js";
import { safeBounce } from "./utils.js";
import { showMapLoading, hideMapLoading } from "../../shared/map/loading-utils.js";

function addMarker(item) {
  const lat = parseFloat(item.acf.industry_lat);
  const long = parseFloat(item.acf.industry_long);

  console.log("📍 Creating marker for:", item.title.rendered, "at", lat, long);

  let marker;
  if (item.acf.is_cluster) {
    marker = L.marker([lat, long], {
      icon: customIcon_2,
      riseOnHover: true,
      alt: item.id,
    });
  } else {
    marker = L.marker([lat, long], {
      icon: customIcon_1,
      riseOnHover: true,
      alt: item.id,
    });
  }

  const title = item.title.rendered;
  const area = parseFloat(item.acf.industry_area);
  const year = parseFloat(item.acf.industry_year);
  const cur_year = new Date().getFullYear();
  let exp = year - cur_year;
  let popupContent = `<div class="popup_content">
            <h4 class="name">${title}</h4>`;
  if (area) {
    popupContent += `<div class="area"><span>${mpsString.area_text}:</span><span>${area} ${mpsString.area_unit_text}</span></div>`;
  }
  if (exp) {
    popupContent += `<div class="exp"><span>${mpsString.exp_text}:</span><span>${exp} ${mpsString.exp_unit_text}</span></div>`;
  }
  popupContent += `</div>`;

  marker.addTo(state.map);
  state.bounds.extend([lat, long]);
  marker.bindTooltip(item.title.rendered, {
    permanent: false,
    direction: "top",
    offset: L.point(0, -50),
  });
  marker.bindPopup(popupContent, {
    offset: [0, -45],
    autoClose: false,
  });

  // Store marker in state.markers array for easy access
  state.markers.push(marker);
  console.log("✅ Marker added to state.markers array. Total markers:", state.markers.length);

  marker.on("click", function () {
    console.log("🎯 Marker clicked:", item.title.rendered);
    safeBounce(marker);

    if (state.navigationActivated) {
      if (state.endPoint) {
        clearTempMarkers();
        state.startPoint = null;
        state.endPoint = null;
      }
      var markerLatLng = marker.getLatLng();
      var tooltip = marker.getTooltip();

      var inputValue;
      if (tooltip && tooltip.getContent()) {
        var txt = document.createElement("textarea");
        txt.innerHTML = tooltip.getContent();
        inputValue = txt.value;
      } else {
        inputValue = `${markerLatLng.lat.toString().slice(0, 5)}, ${markerLatLng.lng.toString().slice(0, 5)}`;
      }

      if (!state.startPoint) {
        state.startPoint = markerLatLng;
        const startPointInput = document.getElementById("start-point");
        startPointInput.value = inputValue;
        startPointInput.setAttribute("title", inputValue);
        els.navMess.classList.add("hidden");
      } else {
        state.endPoint = markerLatLng;
        const endPointInput = document.getElementById("end-point");
        endPointInput.value = inputValue;
        endPointInput.setAttribute("title", inputValue);
        fetchAndDisplayRoute(state.startPoint, state.endPoint);
      }
    }
  });
}

function openAllPopups() {
  for (let marker of state.markers) {
    marker.openPopup();
  }
}

export async function fetchAndDisplayMarkers(apiUrl) {
  console.log("Fetching markers from:", apiUrl); // Debug log

  try {
    const { data } = await ApiService.fetch(apiUrl);
    console.log("Markers data received:", data.length, "items"); // Debug log

    // Check if data has coordinates
    const itemsWithCoordinates = data.filter((item) => {
      const { acf } = item;
      return acf && acf.industry_lat && acf.industry_long;
    });
    console.log("Items with coordinates:", itemsWithCoordinates.length); // Debug log

    data.forEach((item) => {
      const { acf } = item;
      if (acf && acf.industry_lat && acf.industry_long) {
        console.log(
          "Adding marker for:",
          item.title.rendered,
          "at",
          acf.industry_lat,
          acf.industry_long,
        ); // Debug log
        addMarker(item);
      } else {
        console.log("Skipping item without coordinates:", item.title.rendered); // Debug log
      }
    });
    console.log("Total markers added:", state.markers.length); // Debug log
  } catch (error) {
    console.error("Error fetching markers:", error);
  } finally {
    const osm_id = els.mapElement.dataset.osm;
    console.log("OSM ID:", osm_id); // Debug log
    if (osm_id) {
      try {
        console.log("Fetching boundary data for OSM ID:", osm_id); // Debug log
        const response = await fetch(`/wp-content/themes/iphub/osm/${osm_id}.txt`);
        const geojsonData = await response.json();
        console.log("Boundary data fetched successfully"); // Debug log
        state.boundaryLayer = L.geoJSON(geojsonData, {
          style: {
            color: "#dc2626",
            weight: 2,
            fillOpacity: 0.1,
          },
        }).addTo(state.map);

        // Safe bounds fitting with error handling
        try {
          if (state.boundaryLayer && state.boundaryLayer.getBounds) {
            const bounds = state.boundaryLayer.getBounds();
            if (bounds && typeof bounds.isEmpty === "function" && !bounds.isEmpty()) {
              state.map.fitBounds(bounds);
              console.log("Boundary layer added and map fitted"); // Debug log
            } else if (bounds && bounds.getNorthEast && bounds.getSouthWest) {
              // Alternative bounds checking
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              if (ne && sw && ne.lat !== sw.lat && ne.lng !== sw.lng) {
                state.map.fitBounds(bounds);
                console.log("Boundary layer added and map fitted"); // Debug log
              } else {
                console.warn("Boundary bounds are empty, not fitting map");
              }
            } else {
              console.warn("Boundary bounds are empty, not fitting map");
            }
          } else {
            console.warn("Boundary layer not properly initialized");
          }
        } catch (boundsError) {
          console.error("Error fitting boundary bounds:", boundsError);
        }
      } catch (error) {
        console.error("Error fetching boundary data:", error);
      }
    } else {
      console.log("No OSM ID found, fitting bounds to markers"); // Debug log
      // Safe bounds fitting with error handling
      try {
        if (state.bounds && typeof state.bounds.isEmpty === "function" && !state.bounds.isEmpty()) {
          state.map.fitBounds(state.bounds, { maxZoom: 14 });
          console.log("Map fitted to marker bounds"); // Debug log
        } else if (state.bounds && state.bounds.getNorthEast && state.bounds.getSouthWest) {
          // Alternative bounds checking
          const ne = state.bounds.getNorthEast();
          const sw = state.bounds.getSouthWest();
          if (ne && sw && ne.lat !== sw.lat && ne.lng !== sw.lng) {
            state.map.fitBounds(state.bounds, { maxZoom: 14 });
            console.log("Map fitted to marker bounds"); // Debug log
          } else {
            console.log("No bounds available, map not fitted"); // Debug log
          }
        } else {
          console.log("No bounds available, map not fitted"); // Debug log
        }
      } catch (boundsError) {
        console.error("Error fitting marker bounds:", boundsError);
      }
    }
    if (document.body.classList.contains("page-template-page-collection")) {
      openAllPopups();
    }
  }
}

async function fetchAndDisplayAllRegions(regions) {
  const boundaryData = [];

  if (regions) {
    const fetchBoundary = async (osm_id, color) => {
      try {
        const response = await fetch(`/wp-content/themes/iphub/osm/${osm_id}.txt`);
        const geojsonData = await response.json();

        // Extract coordinates for center calculation
        if (geojsonData.features && geojsonData.features.length > 0) {
          const coordinates = geojsonData.features[0].geometry.coordinates;
          if (coordinates && coordinates.length > 0) {
            boundaryData.push(...coordinates[0]); // Add all coordinates from first polygon
          }
        }

        state.boundaryLayer = L.geoJSON(geojsonData, {
          style: {
            color: color,
            weight: 2,
            fillOpacity: 0.3,
          },
        }).addTo(state.map);

        return geojsonData;
      } catch (error) {
        console.error("Error fetching boundary:", error);
        return null;
      }
    };

    const boundaryPromises = [];

    if (typeof regions === "string") {
      if (regions.includes(",")) {
        regions = regions.split(",");
        regions.forEach((osm_id, index) => {
          boundaryPromises.push(fetchBoundary(osm_id, boundaryColor[index]));
        });
      } else {
        boundaryPromises.push(fetchBoundary(regions, boundaryColor[0]));
      }
    } else if (typeof regions === "number") {
      boundaryPromises.push(fetchBoundary(regions, boundaryColor[0]));
    }

    await Promise.all(boundaryPromises);
  }

  return boundaryData;
}

async function loadLogisticLocations() {
  const apiUrl = `${window.location.origin}/wp-json/wp/v2/mps_logistic?per_page=100`;
  try {
    const { data } = await ApiService.fetch(apiUrl);

    data.forEach(function (logistic) {
      let basePath = `${window.location.origin}/wp-content/themes/iphub/img/`;
      let iconUrl = basePath + "default_marker.png";

      if (logistic.mps_logistic_cat.includes(110) || logistic.mps_logistic_cat.includes(117)) {
        iconUrl = basePath + "anchor.svg";
      } else if (logistic.mps_logistic_cat.includes(118)) {
        iconUrl = basePath + "gate.svg";
      } else if (logistic.mps_logistic_cat.includes(119)) {
        iconUrl = basePath + "airport.svg";
      } else if (logistic.mps_logistic_cat.includes(120)) {
        iconUrl = basePath + "iland.svg";
      }
      const icon = L.icon({
        iconUrl: iconUrl,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -24],
      });

      if (logistic.acf.lat && logistic.acf.long) {
        const marker = L.marker([logistic.acf.lat, logistic.acf.long], {
          icon: icon,
        }).addTo(state.map);
        marker.bindTooltip(logistic.title.rendered, {
          permanent: false,
          direction: "top",
          offset: L.point(0, -30),
        });
        marker.on("click", function () {
          safeBounce(marker);
          if (state.navigationActivated) {
            if (state.endPoint) {
              clearTempMarkers();
              state.startPoint = null;
              state.endPoint = null;
            }
            var markerLatLng = marker.getLatLng();
            var tooltip = marker.getTooltip();

            var inputValue;
            if (tooltip && tooltip.getContent()) {
              var txt = document.createElement("textarea");
              txt.innerHTML = tooltip.getContent();
              inputValue = txt.value;
            } else {
              inputValue = `${markerLatLng.lat.toString().slice(0, 5)}, ${markerLatLng.lng.toString().slice(0, 5)}`;
            }

            if (!state.startPoint) {
              state.startPoint = markerLatLng;
              const startPointInput = document.getElementById("start-point");
              startPointInput.value = inputValue;
              startPointInput.setAttribute("title", inputValue);
              els.navMess.classList.add("hidden");
            } else {
              state.endPoint = markerLatLng;
              const endPointInput = document.getElementById("end-point");
              endPointInput.value = inputValue;
              endPointInput.setAttribute("title", inputValue);
              fetchAndDisplayRoute(state.startPoint, state.endPoint);
            }
          }
        });
      }
    });
  } catch (error) {
    console.error("Error loading logistic locations", error);
  }
}

export async function loadMapData() {
  console.log("=== LOADMAPDATA FUNCTION CALLED ==="); // Debug log
  console.log("Loading map data..."); // Debug log

  // Check if map is available
  if (!state.map) {
    console.error("Map not available in state, cannot load data");
    return;
  }

  console.log("Map is available, proceeding with data loading..."); // Debug log

  // Show loading indicator using shared utility
  showMapLoading("Đang tải dữ liệu bản đồ...");

  try {
    loadLogisticLocations();
    const regions = els.mapElement.dataset.regions;
    console.log("Regions data:", regions); // Debug log

    if (document.body.classList.contains("tax-industry_location")) {
      const locationId = els.mapElement.dataset.location;
      console.log("Location ID:", locationId); // Debug log
      const apiUrl = `${window.location.origin}/wp-json/wp/v2/industry?industry_location=${locationId}&per_page=200`;
      console.log("API URL:", apiUrl); // Debug log
      console.log("Calling fetchAndDisplayMarkers..."); // Debug log
      await fetchAndDisplayMarkers(apiUrl);
      console.log("Calling fetchAndDisplayAllRegions..."); // Debug log
      await fetchAndDisplayAllRegions(regions);
    }
    if (
      document.body.classList.contains("page-template-page-collection") ||
      document.body.classList.contains("search")
    ) {
      const collectionID = els.industries.dataset.in;
      if (collectionID) {
        const apiUrl = `${window.location.origin}/wp-json/wp/v2/industry/?include=${collectionID}&per_page=200`;
        await fetchAndDisplayMarkers(apiUrl);
        await fetchAndDisplayAllRegions(regions);
      }
    }
  } catch (error) {
    console.error("Error loading map data:", error);
  } finally {
    // Hide loading indicator using shared utility
    hideMapLoading();
    console.log("Loading indicator hidden"); // Debug log
  }

  console.log("=== LOADMAPDATA FUNCTION COMPLETED ==="); // Debug log
}
